import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { mspApi } from '../../services/api';
import { markOnboardingStepComplete } from '../../services/onboardingSync';
import ReturnBanner from '../../components/ReturnBanner';
import './Msp.css';

const normalizeTenants = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tenants)) return payload.tenants;
  return [];
};

function MspDashboard() {
  const [tenants, setTenants] = useState([]);
  const [status, setStatus] = useState('loading');
  const [scanStatus, setScanStatus] = useState('idle');

  useEffect(() => {
    let isMounted = true;

    mspApi
      .listTenants()
      .then((response) => {
        if (!isMounted) return;
        const tenantList = normalizeTenants(response.data);
        setTenants(tenantList);
        setStatus('ready');
        
        // Sync Onboarding: If at least one tenant exists, "add_tenant" is complete
        if (tenantList.length > 0) {
          markOnboardingStepComplete('add_tenant');
        }
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const totals = useMemo(() => {
    return tenants.reduce(
      (acc, tenant) => {
        acc.assets += tenant.assets_count || tenant.assets || 0;
        acc.reports += tenant.reports_count || tenant.report_count || 0;
        acc.alerts += tenant.alerts_count || tenant.alert_count || 0;
        return acc;
      },
      { assets: 0, reports: 0, alerts: 0 }
    );
  }, [tenants]);

  const portfolioTrend = useMemo(() => {
    const trendBuckets = [];
    tenants.forEach((tenant) => {
      const trend = tenant.risk_trend || tenant.riskTrend || tenant.trend || [];
      if (!Array.isArray(trend)) return;
      trend.forEach((point, index) => {
        const value = typeof point === 'number' ? point : point?.value || point?.risk_score;
        if (typeof value !== 'number') return;
        if (!trendBuckets[index]) {
          trendBuckets[index] = { index, sum: 0, count: 0 };
        }
        trendBuckets[index].sum += value;
        trendBuckets[index].count += 1;
      });
    });
    return trendBuckets.map((bucket) => ({
      label: `T-${trendBuckets.length - bucket.index}`,
      value: bucket.count ? Math.round(bucket.sum / bucket.count) : 0,
    }));
  }, [tenants]);

  const topRiskTenants = useMemo(() => {
    const withDelta = tenants.map((tenant) => {
      const trend = tenant.risk_trend || tenant.riskTrend || tenant.trend || [];
      const values = Array.isArray(trend)
        ? trend
            .map((point) => (typeof point === 'number' ? point : point?.value || point?.risk_score))
            .filter((value) => typeof value === 'number')
        : [];
      const latest = values[values.length - 1] ?? tenant.risk_score ?? tenant.riskScore ?? 0;
      const previous = values[values.length - 2] ?? latest;
      return {
        ...tenant,
        riskDelta: latest - previous,
        riskValue: tenant.risk_score || tenant.riskScore || latest,
      };
    });
    return withDelta
      .sort((a, b) => b.riskDelta - a.riskDelta)
      .slice(0, 3);
  }, [tenants]);

  const coverageMetrics = useMemo(() => {
    const now = Date.now();
    const thirtyDays = 1000 * 60 * 60 * 24 * 30;
    let scannedRecently = 0;
    let slaMet = 0;
    tenants.forEach((tenant) => {
      const lastScan = tenant.last_scan_at || tenant.lastScan;
      if (lastScan && now - new Date(lastScan).getTime() <= thirtyDays) {
        scannedRecently += 1;
      }
      if (tenant.sla_met || tenant.slaMet) {
        slaMet += 1;
      }
    });
    return {
      scanCoverage: tenants.length ? Math.round((scannedRecently / tenants.length) * 100) : 0,
      slaCoverage: tenants.length ? Math.round((slaMet / tenants.length) * 100) : 0,
    };
  }, [tenants]);

  const handleTriggerPortfolioScan = async () => {
    setScanStatus('submitting');
    try {
      await mspApi.triggerPortfolioScan();
      setScanStatus('success');
    } catch (error) {
      console.error('Unable to trigger portfolio scan', error);
      setScanStatus('error');
    }
  };

  return (
    <>
      <ReturnBanner />
      <div className="msp-page">
        <div className="msp-header">
        <div>
          <h1>MSP Command Center</h1>
          <p className="msp-subtitle">Portfolio health across onboarded tenants</p>
        </div>
        <span className="msp-pill">Total Tenants: {tenants.length}</span>
      </div>

      <div className="msp-grid">
        <div className="msp-card">
          <span className="msp-card-label">Managed Assets</span>
          <span className="msp-card-value">{totals.assets}</span>
        </div>
        <div className="msp-card">
          <span className="msp-card-label">Active Reports</span>
          <span className="msp-card-value">{totals.reports}</span>
        </div>
        <div className="msp-card">
          <span className="msp-card-label">Open Alerts</span>
          <span className="msp-card-value">{totals.alerts}</span>
        </div>
        <div className="msp-card">
          <span className="msp-card-label">Scan Coverage (30d)</span>
          <span className="msp-card-value">{coverageMetrics.scanCoverage}%</span>
          <span className="msp-card-label">SLA Adherence</span>
          <span className="msp-card-value">{coverageMetrics.slaCoverage}%</span>
        </div>
      </div>

      <div className="msp-grid">
        <div className="msp-card">
          <span className="msp-card-label">Portfolio Risk Trend</span>
          <div className="msp-chart-card">
            {portfolioTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={portfolioTrend}>
                  <defs>
                    <linearGradient id="portfolioTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00f3ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#00f3ff" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" stroke="#8a94a6" />
                  <YAxis stroke="#8a94a6" />
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#00f3ff" fill="url(#portfolioTrend)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="msp-empty">Trend data unavailable.</div>
            )}
          </div>
        </div>
        <div className="msp-card">
          <span className="msp-card-label">Top At-Risk Tenants</span>
          {topRiskTenants.length === 0 ? (
            <div className="msp-empty">No delta data yet.</div>
          ) : (
            <div className="msp-list">
              {topRiskTenants.map((tenant) => {
                const tenantId = tenant.id || tenant.tenant_id;
                const tenantName = tenant.name || tenant.tenant_name || tenantId;
                return (
                  <Link key={tenantId} to={`/msp/tenants/${tenantId}`} className="msp-list-item">
                    <div>
                      <div className="msp-list-title">{tenantName}</div>
                      <div className="msp-list-meta">Risk: {tenant.riskValue}</div>
                    </div>
                    <span className={`msp-badge ${tenant.riskDelta >= 0 ? 'msp-badge-warn' : 'msp-badge-good'}`}>
                      {tenant.riskDelta >= 0 ? `+${tenant.riskDelta}` : tenant.riskDelta}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        <div className="msp-card">
          <span className="msp-card-label">Quick Actions</span>
          <div className="msp-action-stack">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleTriggerPortfolioScan}
              disabled={scanStatus === 'submitting'}
            >
              {scanStatus === 'submitting' ? 'Launching…' : 'Trigger Portfolio Scan'}
            </button>
            {tenants.length > 0 && (
              <Link
                className="btn btn-secondary"
                to={`/msp/tenants/${tenants[0].id || tenants[0].tenant_id}/findings`}
              >
                Open Urgent Findings
              </Link>
            )}
            <Link className="btn btn-secondary" to="/msp/tenants">
              Review Tenants
            </Link>
          </div>
          {scanStatus === 'error' && <div className="msp-empty">Portfolio scan failed.</div>}
        </div>
      </div>

      <div className="msp-section">
        <div className="msp-section-title">Tenant Watchlist</div>
        {status === 'loading' && <div className="msp-empty">Loading tenant telemetry…</div>}
        {status === 'error' && (
          <div className="msp-empty">Unable to load tenants. Check connectivity.</div>
        )}
        {status === 'ready' && tenants.length === 0 && (
          <div className="msp-empty">No tenants detected yet.</div>
        )}
        {status === 'ready' && tenants.length > 0 && (
          <table className="msp-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Assets</th>
                <th>Reports</th>
                <th>Alerts</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => {
                const tenantId = tenant.id || tenant.tenant_id;
                const tenantName = tenant.name || tenant.tenant_name || tenantId;
                return (
                  <tr key={tenantId}>
                    <td>{tenantName}</td>
                    <td>{tenant.assets_count || tenant.assets || 0}</td>
                    <td>{tenant.reports_count || tenant.report_count || 0}</td>
                    <td>{tenant.alerts_count || tenant.alert_count || 0}</td>
                    <td>
                      <Link className="msp-link" to={`/msp/tenants/${tenantId}`}>
                        View Detail
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </>
  );
}

export default MspDashboard;
